import type { ApiResponse } from '../../types'
import type {
  FriendDto,
  DirectoryEntryDto,
  ApprovalItemDto,
  ActivityEventDto,
  CreateFriendRequest,
  ParentNoteRequest,
  ModeratorMessageRequest,
} from '../../types/community'
import { client, unwrap, apiError, CotApiError } from './client'

// COTC_FriendsApprovals — real bodies over the shared community axios instance.

async function run<T>(op: () => Promise<ApiResponse<T>>): Promise<T> {
  try {
    return unwrap(await op())
  } catch (err) {
    if (err instanceof CotApiError) throw err
    throw apiError(err)
  }
}

/** POST /friend-requests — connect two persons (both parents approve). */
export async function createFriendRequest(req: CreateFriendRequest): Promise<{ id: string; state: string }> {
  return run(async () => (await client.post<ApiResponse<{ id: string; state: string }>>('/friend-requests', req)).data)
}

/** GET /friends — friends of the (server-held) acting person in a circle. */
export async function listFriends(circleId?: string): Promise<FriendDto[]> {
  return run(async () =>
    (await client.get<ApiResponse<FriendDto[]>>('/friends', { params: { circleId } })).data,
  )
}

/** GET /find — the friend directory for the (server-held) acting person. */
export async function findFriends(filter?: string): Promise<DirectoryEntryDto[]> {
  return run(async () =>
    (await client.get<ApiResponse<DirectoryEntryDto[]>>('/find', { params: { filter } })).data,
  )
}

/** GET /approvals — the guardian's pending-decision queue (union). */
export async function getApprovals(): Promise<ApprovalItemDto[]> {
  return run(async () => (await client.get<ApiResponse<ApprovalItemDto[]>>('/approvals')).data)
}

/** POST /approvals/{kind}/{id}/approve|decline. */
export async function decideApproval(
  kind: string,
  id: string,
  decision: 'approve' | 'decline',
): Promise<void> {
  await run(async () => (await client.post<ApiResponse>(`/approvals/${kind}/${id}/${decision}`)).data)
}

/** GET /activity — merged family + circle feed. */
export async function getActivity(): Promise<ActivityEventDto[]> {
  return run(async () => (await client.get<ApiResponse<ActivityEventDto[]>>('/activity')).data)
}

/** POST /activity/ping — kid "Ping my circle" (acting person is server-held). */
export async function pingCircle(): Promise<void> {
  await run(async () => (await client.post<ApiResponse>('/activity/ping', {})).data)
}

/** POST /messages/parent — parent-to-parent note. */
export async function sendParentNote(req: ParentNoteRequest): Promise<void> {
  await run(async () => (await client.post<ApiResponse>('/messages/parent', req)).data)
}

/** POST /moderator-messages — "Message a moderator". */
export async function sendModeratorMessage(req: ModeratorMessageRequest): Promise<void> {
  await run(async () => (await client.post<ApiResponse>('/moderator-messages', req)).data)
}
